/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useMemo } from "react";
import DocumentTitle from "react-document-title";
import TreeView from "react-accessible-treeview";
import { IoMdArrowDropright } from "react-icons/io";
import { AiOutlineLoading } from "react-icons/ai";
import cx from "classnames";
import moment from "moment";
import styled from "styled-components";

import SectionTable, { SectionBody } from "styles/components/SectionTable";
import { IMAGE_S3_SERVER, restApiBaseUrl } from "constants/api";
import IconSvgComponent from "components/common/IconSvgComponent";
import { FormattedText } from "../common/FormattedText";
import { FrameBox } from "styles/components/Frame";
import {
  StudentInfoQuestion,
  StudentInfoQuestionHead,
  StudentInfoQuestionList,
  StudentInfoQuestionListItem,
  StudentInfoQuestionListIndicator,
} from "styles/components/StudentInfo";
import {
  AssessmentQuestionNumber,
  AssessmentQuestionImage,
  AssessmentQuestionText,
  OptionImage,
  AnswerLabel,
  AnswerImage,
  AnswerText,
} from "styles/components/AssessmentQuestions";
import { addNotification } from "store/state/ui/actions";
import { LEVELS, createNotification } from "utilities/notification";
import Field from "components/common/Field";
import { getAccessToken } from "utilities/localStore";
import { NoDataComponent, VirtualTable } from "components/common/table";
import { FORMAT } from "constants/common";
import Tooltip from "styles/components/Tooltip";
// import { read, stream, utils, writeFile } from "xlsx";

const apiUrl = restApiBaseUrl;

const Button = styled.button`
  position: relative;
  color: white;
  border-radius: 5px;
  padding: 6px 12px;
  outline: none;
  border: 1px solid #4e68eb;
  background-color: #4e68eb;
  box-shadow: 1px 3px 3px -1px #888;
  transition: background-color 150ms linear;
  min-width: ${(props) => {
    const xPadding = 24;
    const contentLength = Math.max(props.children.length, props.loadingText?.length ?? 0);
    const minWidth = xPadding + contentLength * 8;

    return `${minWidth}px`;
  }};

  :hover {
    background-color: #1f49e6;
    border-color: #1f49e6;
  }

  :active {
    box-shadow: 0px 2px 3px -2px #888;
  }

  &[disabled],
  &[disabled]:hover,
  &[disabled]:active {
    cursor: not-allowed;
    opacity: 0.7;
    border: 1px solid #4e68eb;
    background-color: #4e68eb;
    box-shadow: 1px 3px 3px -1px #888;
  }

  ${(props) =>
    props.variant === "outlined" &&
    `
      color: #333;
      background-color: #fff;
      border: 1px solid #4e68eb;
      box-shadow: 1px 3px 3px -2px #4e68eb;

      :hover {
        background-color: #d7edff;
        border-color: #1f49e6;
      }

      :active {
        box-shadow: 0px 2px 3px -3px #4e68eb;
      }
  `}

  ${(props) =>
    props.variant === "icon" &&
    `
      padding: 6px 8px;
      padding-bottom: 3px;
      color: #333;
      background-color: #fff;
      border: 1px solid #4e68eb;
      box-shadow: 1px 3px 3px -2px #4e68eb;


      :hover {
        background-color: #d7edff;
        border-color: #1f49e6;
      }

      :active {
        box-shadow: 0px 2px 3px -3px #4e68eb;
      }

      &[disabled],
      &[disabled]:hover,
      &[disabled]:active {
        cursor: not-allowed;
        opacity: 0.7;
        color: #333;
        background-color: #fff;
        border: 1px solid #4e68eb;
        box-shadow: 1px 3px 3px -2px #4e68eb;
      }
  `}

  &::after {
    content: ${(props) => props.loadingText && `"${props.loadingText}"`};
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
  }

  ${(props) =>
    props.isLoading &&
    `
      pointer-events: none;
      background-color: #1cc379 !important;
      border: 1px solid #1cc379 !important;

      &::after {
        background-color: #1cc379;
        opacity: 1;
      }
  `}
`;

const ArrowIcon = ({ isOpen, className }) => {
  const baseClass = "arrow";
  const classes = cx(
    baseClass,
    { [`${baseClass}--closed`]: !isOpen },
    { [`${baseClass}--open`]: isOpen },
    className
  );
  return <IoMdArrowDropright className={classes} style={{ marginBottom: "-2px" }} />;
};

const getDisplayType = (data) => {
  let display = "radio";
  if (data.find((i) => i.type === "IMAGE") != undefined) {
    display = "image";
  }
  return display;
};

const HoverCardTrigger = styled.div``;
const HoverCardContent = styled.div``;
const HoverCard = styled.div`
  ${HoverCardTrigger} {
    cursor: default;
  }

  ${HoverCardContent} {
  }

  &:hover {
    ${HoverCardTrigger} {
      text-decoration: underline;
    }

    ${HoverCardContent} {
      /* display: block;
      animation: fadeIn 350ms ease-in-out forwards; */
    }
  }
`;

const QuestionCard = ({ question }) => {
  return (
    <SectionBody
      style={{
        position: "relative",
        zIndex: 1000,
        color: "#333",
        flexGrow: 1,
        padding: "8px 16px",
        border: "1px solid #1f49e6",
      }}
    >
      <FrameBox
        style={{
          border: "none",
          boxShadow: "none",
        }}
      >
        <StudentInfoQuestion>
          <StudentInfoQuestionHead>
            <AssessmentQuestionImage fullwidth style={{ textAlign: "left" }}>
              {question.description?.map((dataItem, k) => {
                if (dataItem.type === "TEXT") {
                  return (
                    <AssessmentQuestionText
                      key={k}
                      layout={question.layout}
                      widthCounter={question.description.length}
                      floatCounter={k}
                      style={{ textAlign: "left" }}
                    >
                      {k == 0 && (
                        <AssessmentQuestionNumber style={{ textAlign: "left" }}>
                          {question.assessment_content.display_order}.
                        </AssessmentQuestionNumber>
                      )}
                      <FormattedText text={dataItem.text} />
                    </AssessmentQuestionText>
                  );
                }
                return (
                  <AssessmentQuestionText
                    key={k}
                    layout={question.layout}
                    widthCounter={question.description.length}
                    floatCounter={k}
                  >
                    {k == 0 && (
                      <AssessmentQuestionNumber>
                        {question.assessment_content.display_order}.
                      </AssessmentQuestionNumber>
                    )}
                    <OptionImage
                      widthCounter={question.description.length}
                      layout={question.layout}
                    >
                      <img key={k} src={`${IMAGE_S3_SERVER}${dataItem.src}`} alt={dataItem.src} />
                    </OptionImage>
                  </AssessmentQuestionText>
                );
              })}
            </AssessmentQuestionImage>
          </StudentInfoQuestionHead>

          <StudentInfoQuestionList>
            {question.answers?.map((answer, aIndex) => (
              <StudentInfoQuestionListItem
                key={aIndex}
                correct={answer.is_correct}
                selected={answer.isSelected}
                containsImage={answer.description[0]?.type === "IMAGE"}
              >
                <span style={{ marginRight: "12px" }}>{answer.display_order}</span>
                <StudentInfoQuestionListIndicator
                  correct={answer.is_correct}
                  selected={answer.isSelected}
                >
                  {answer.is_correct && (
                    <IconSvgComponent additionalClass="correct" iconPath="svg/ico-correct.svg" />
                  )}
                  {question.isAttempted && !question.is_correct && answer.isSelected && (
                    <IconSvgComponent additionalClass="correct" iconPath="svg/ico-incorrect.svg" />
                  )}
                </StudentInfoQuestionListIndicator>
                <AnswerLabel
                  displayType={getDisplayType(answer.description)}
                  quizType="assessment"
                  layout={answer.layout}
                >
                  <AnswerImage>
                    {answer.description?.map((data, k) => {
                      if (data.type === "TEXT") {
                        return (
                          <AnswerText
                            key={k}
                            layout={answer.layout}
                            widthCounter={answer.description.length}
                            floatCounter={k}
                          >
                            <FormattedText text={data.text}></FormattedText>
                          </AnswerText>
                        );
                      } else {
                        return (
                          <OptionImage
                            key={k}
                            widthCounter={answer.description.length}
                            layout={answer.layout}
                          >
                            {data.src.trim() ? (
                              <img key={k} src={`${IMAGE_S3_SERVER}${data.src}`} alt={data.src} />
                            ) : null}
                          </OptionImage>
                        );
                      }
                    })}
                  </AnswerImage>
                </AnswerLabel>
              </StudentInfoQuestionListItem>
            ))}
          </StudentInfoQuestionList>
        </StudentInfoQuestion>
      </FrameBox>
    </SectionBody>
  );
};

const deploymentColumns = [
  {
    id: "name",
    Header: "Deployment Name",
    accessor: "name",
    minWidth: 200,
    maxWidth: 300,
  },
  {
    id: "assessment",
    Header: "Assessment",
    accessor: (row) => row.assessment_series?.name ?? "—",
    minWidth: 200,
    maxWidth: 250,
  },
  {
    id: "cohort",
    Header: "Cohort",
    accessor: (row) => row.program_cohort_enrollment?.cohort?.name ?? "—",
    minWidth: 150,
    maxWidth: 200,
  },
  {
    id: "start_date",
    accessor: (row) => (row.start_date ? new Date(row.start_date).toLocaleDateString() : "—"),
    Header: "Start",
    minWidth: 100,
  },
  {
    id: "end_date",
    accessor: (row) => (row.end_date ? new Date(row.end_date).toLocaleDateString() : "—"),
    Header: "End",
    minWidth: 100,
  },
  {
    id: "is_access_code_required",
    Header: "Access Code",
    accessor: "is_access_code_required",
    minWidth: 100,
    Cell: (props) => {
      return <div>{String(props.value)}</div>;
    },
  },
  {
    id: "is_draft",
    Header: "Draft",
    accessor: "is_draft",
    minWidth: 100,
    Cell: (props) => {
      return <div>{String(props.value)}</div>;
    },
  },
];

// - sum - Sums the values in the grouped rows.
// - count - Counts the number of rows in the grouped rows.
// - min - Finds the minimum value in the grouped rows.
// - max - Finds the maximum value in the grouped rows.
// - extent - Finds the extent (min and max) of the values in the grouped rows.
// - mean - Finds the mean of the values in the grouped rows.
// - median - Finds the median of the values in the grouped rows.
// - unique - Returns an array of unique values in the grouped rows.
// - uniqueCount - Counts the number of unique values in the grouped rows.

const attemptsGroupedBySeriesColumns = [
  {
    id: "username",
    accessor: "username",
    Header: "Username",
    Aggregated: ({ value }) => {
      if (value === 0) {
        return "No students";
      } else if (value === 1) {
        return "1 student";
      } else {
        return `${value} students`;
      }
    },
    aggregate: "uniqueCount",
    minWidth: 150,
    maxWidth: 250,
  },
  {
    id: "series",
    accessor: (row) => row.series?.name,
    Header: "Series",
    // Aggregated: ({ value }) => "",
    aggregate: "count",
    minWidth: 175,
    // maxWidth: 200,
  },
  {
    id: "version",
    accessor: (row) => row.version?.version ?? "—",
    Header: "Version",
    Cell: ({ value }) => `Version ${value}`,
    Aggregated: ({ value }) => {
      if (value === 0) {
        return "No versions";
      } else if (value === 1) {
        return "1 version";
      } else {
        return `${value} versions`;
      }
    },
    aggregate: "uniqueCount",
    minWidth: 125,
    maxWidth: 150,
  },
  {
    id: "attemptNumber",
    accessor: "attempt_number",
    Header: "Atempts",
    Aggregated: ({ value }) => `${value} (total)`,
    Cell: ({ value }) => `#${value}`,
    aggregate: "count",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    id: "score",
    accessor: (row) => row.questions_correct,
    Header: "Score",
    Aggregated: ({ value }) => `${Math.round(value)} (avg)`,
    aggregate: "average",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    id: "date",
    accessor: (row) => new Date(row.start_time).toLocaleDateString(),
    Header: "Date",
    Aggregated: ({ value }) => "",
    aggregate: "uniqueCount",
    minWidth: 150,
  },
];

const attemptsGroupedByVersionColumns = [
  {
    id: "username",
    accessor: "username",
    Header: "Username",
    Aggregated: ({ value }) => {
      if (value === 0) {
        return "No students";
      } else if (value === 1) {
        return "1 student";
      } else {
        return `${value} students`;
      }
    },
    aggregate: "uniqueCount",
    minWidth: 150,
    maxWidth: 250,
  },
  {
    id: "version",
    accessor: (row) => row.version.version,
    Header: "Version",
    Cell: ({ value }) => `Version ${value}`,
    Aggregated: ({ value }) => {
      if (value === 0) {
        return "No versions";
      } else if (value === 1) {
        return "1 version";
      } else {
        return `${value} versions`;
      }
    },
    aggregate: "uniqueCount",
    minWidth: 150,
    maxWidth: 200,
  },
  {
    id: "attemptNumber",
    accessor: "attempt_number",
    Header: "Atempts",
    Aggregated: ({ value }) => `${value} (total)`,
    Cell: ({ value }) => `#${value}`,
    aggregate: "count",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    id: "score",
    accessor: (row) => row.questions_correct,
    Header: "Score",
    Aggregated: ({ value }) => `${Math.round(value)} (avg)`,
    aggregate: "average",
    minWidth: 100,
    maxWidth: 150,
  },
  {
    id: "date",
    accessor: (row) => new Date(row.start_time).toLocaleDateString(),
    Header: "Date",
    Aggregated: ({ value }) => "",
    aggregate: "uniqueCount",
    minWidth: 150,
  },
];

const responsesGroupedByAttemptColumns = [
  {
    id: "username",
    accessor: (row) => row.attempt.username,
    Header: "Username",
    minWidth: 150,
    maxWidth: 250,
  },
  {
    id: "attempt_number",
    accessor: (row) => row.attempt.attempt_number,
    Header: "Attempt #",
    Cell: (props) => {
      if (props.row.original) {
        return <span title={`ID: ${props.row.original.attempt.id}`}>{props.value}</span>;
      }

      return <span>{props.value}</span>;
    },
    minWidth: 150,
    maxWidth: 250,
  },
  {
    id: "question_number",
    accessor: (row) => row.question.assessment_content.display_order,
    Header: "Question #",
    Cell: (props) => {
      if (props.row.original) {
        return (
          <HoverCard>
            <HoverCardTrigger
              title={`ID: ${props.row.original.question.id}`}
              data-tooltip-id={props.row.original.question.id}
            >
              {props.value}
            </HoverCardTrigger>

            <Tooltip
              // isOpen={props.row.original.question.id == 178}
              id={props.row.original.question.id}
              className="question-card"
              positionStrategy="fixed"
              place="right"
              opacity={1}
              offset={0}
            >
              <QuestionCard question={props.row.original.question} />
            </Tooltip>
          </HoverCard>
        );
      }

      return <span>{props.value}</span>;
    },
    minWidth: 150,
    maxWidth: 200,
  },
  {
    id: "answer_number",
    accessor: (row) => row.answer.display_order,
    Header: "Answer #",
    Cell: (props) => {
      if (props.row.original) {
        return (
          <span
            title={
              props.row.original.answer.id
                ? `ID: ${props.row.original.answer.id}`
                : "No answer selected"
            }
          >
            {props.value ?? "—"}
          </span>
        );
      }

      return <span>{props.value}</span>;
    },
    minWidth: 150,
    maxWidth: 200,
  },
  {
    id: "correct_answer",
    accessor: (row) => {
      return row.question?.answers?.find((a) => a.is_correct)?.display_order;
    },
    Header: "Correct Answer #",
    minWidth: 150,
    maxWidth: 200,
  },
  {
    id: "itemScore",
    accessor: (row) => (row.is_correct ? 1 : 0),
    Header: "Score",
    aggregate: (values) => {
      const sum = values.reduce((accumulator, currentValue) => accumulator + currentValue);
      return `${sum} / ${values.length}`;
    },
    minWidth: 150,
    maxWidth: 200,
  },
];

const getData = (url) => {
  return fetch(url, {
    mode: "cors",
    method: "get",
    headers: {
      Authorization: getAccessToken(),
      "Content-Type": "application/json",
    },
  }).then((response) => response.json());
};

const fetchLargeDataset = async ({ url, searchParams, pageSize = 1000 }) => {
  try {
    let rows = [];

    const params = searchParams ?? new URLSearchParams();
    params.append(`page`, 1);
    params.append(`pageSize`, pageSize);
    const urlWithParams = `${url}?${params.toString()}`;
    const firstPage = await getData(urlWithParams);
    rows.push(...firstPage.data);

    const countRemainingToFetch = firstPage.count - pageSize;
    const pagesRemainingToFetch =
      countRemainingToFetch > 0 ? Math.ceil(countRemainingToFetch / pageSize) : 0;

    let batchPromises = [];
    for (let i = 2; i <= pagesRemainingToFetch + 1; i++) {
      let batchUrl = `${url}?${searchParams.toString()}&page=${i}&pageSize=${pageSize}`;
      batchPromises.push(getData(batchUrl));
    }

    const batchResults = await Promise.all(batchPromises);

    batchResults.forEach((batchResult) => rows.push(...batchResult.data));

    return rows;
  } catch (error) {
    console.error(error);
    return { status: "error" };
  }
};

const ExportWorker = `
  import * as XLSX from "https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs";

  self.addEventListener(
    "message",
    (e) => {
      try {
        const { rows, headers, orderedKeys, columnWidths } = e.data;

        /* Create a new workbook from the data */
        const ws = XLSX.utils.json_to_sheet(rows, {
          header: orderedKeys,
          dense: true, // https://github.com/SheetJS/sheetjs/issues/2750
        });
        XLSX.utils.sheet_add_aoa(ws, [orderedKeys.map((key) => headers[key])], {
          origin: "A1",
        });
        ws["!cols"] = columnWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Export Data");

        /* Write XLSB data (Uint8Array) */
        const u8 = XLSX.write(wb, { bookType: "csv", type: "buffer" });

        /* Generate URL */
        const url = URL.createObjectURL(new Blob([u8]));

        /* Reply with result */
        postMessage({ url });
      } catch (e) {
        /* Pass the error message back */
        postMessage({ error: String(e.message || e).bold() });
      }
    },
    false
  );
`;

const AdminPage = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [navTree, setNavTree] = useState([]);
  const [activeNodeId, setActiveNodeId] = useState(1);
  const [tableState, setTableState] = useState({
    isLoading: false,
    data: [],
    columns: [],
    additionalProps: {},
  });
  // Load accounts for account select
  const [accounts, setAccounts] = useState([]);
  // Keep track of the currently selected account used for queries
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  // Fetch all deployments for an account when selecting an accountId
  const [deployments, setDeployments] = useState({});
  const [series, setSeries] = useState({});
  const [versions, setVersions] = useState({});
  const [attempts, setAttempts] = useState({});
  const [responses, setResponses] = useState({});

  const tableTitle = useMemo(() => {
    if (!activeNodeId) {
      return "Data";
    }

    if (activeNodeId === 1) {
      return "Deployments";
    }

    const matchingNode = navTree.find((node) => node.id === activeNodeId);
    if (matchingNode) {
      const { originalId, type } = matchingNode;

      if (type === "series") {
        return `${matchingNode.name} Data`;
      }

      if (type === "version") {
        const matchingRecord = versions[originalId];
        return `${matchingRecord.name} Data`;
      }

      if (type === "deployment") {
        return `${matchingNode.name} Data`;
      }
    }

    return "Data";
  }, [navTree, activeNodeId, versions]);

  // Fetch accounts on mount
  useEffect(() => {
    if (accounts.length === 0) {
      setIsFetching(true);
      const url = `${apiUrl}/rest/accounts`;
      fetch(url, {
        mode: "cors",
        method: "get",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((result) => {
          setAccounts(result.data);
          setIsFetching(false);
        });
    }
  }, [accounts.length]);

  const onLoadData = async (props) => {
    if (props.element.type === "root") {
      return;
    }

    if (props.element.type === "deployment") {
      const neededSeriesIds = Object.values(deployments)
        .filter((d) => d.pce_id === props.element.originalId)
        .reduce((a, d) => {
          if (a.includes(d.assessment_series_id)) {
            return a;
          } else {
            return a.concat(d.assessment_series_id);
          }
        }, []);

      const haveSeriesForDeployment = neededSeriesIds.every((seriesId) => !!series[seriesId]);

      if (!haveSeriesForDeployment) {
        const params = new URLSearchParams();
        params.append("expand[0]", "versions");
        params.append("expand[1]", "content");
        neededSeriesIds.forEach((seriesId, index) => {
          params.append(`ids[${index}]`, seriesId);
        });
        const url = `${apiUrl}/rest/assessment-series?${params.toString()}`;
        fetch(url, {
          mode: "cors",
          method: "get",
          headers: {
            Authorization: getAccessToken(),
            "Content-Type": "application/json",
          },
        })
          .then((response) => response.json())
          .then((result) => {
            setNavTree((previousNavTree) => {
              // Update local state with fetched series
              setSeries((previousSeries) => {
                result.data.forEach((series) => {
                  const { assessments, ...seriesData } = series;
                  previousSeries[seriesData.id] = seriesData;
                });

                return previousSeries;
              });

              // Update local state with fetched versions
              let seriesIds = [];
              setVersions((previousVersions) => {
                result.data.forEach((series) => {
                  const { assessments } = series;
                  let seriesNodeId = `series-${series.id}-for-deployment-${props.element.originalId}`;
                  let versionIds = [];

                  assessments.forEach((version) => {
                    previousVersions[version.id] = version;
                    let versionNodeId = `version-${version.id}-for-series-${series.id}-for-deployment-${props.element.originalId}`;

                    // Update tree navigation with version nodes
                    versionIds.push(versionNodeId);
                    previousNavTree.push({
                      type: "version",
                      originalId: version.id,
                      name: `Version ${version.version}`,
                      id: versionNodeId,
                      parent: seriesNodeId,
                      children: [],
                      iconPath: "svg/assessment.svg",
                      isBranch: false,
                    });
                  });

                  // Update tree navigation with series nodes
                  seriesIds.push(seriesNodeId);
                  previousNavTree.push({
                    type: "series",
                    originalId: series.id,
                    name: series.name,
                    id: seriesNodeId,
                    parent: props.element.id,
                    children: versionIds,
                    iconPath: "svg/assessment.svg",
                    isBranch: true,
                  });
                });

                return previousVersions;
              });

              // Update tree navigation pce node with series children
              const newTree = previousNavTree.map((node) => {
                if (node.id === props.element.id) {
                  return {
                    ...node,
                    children: node.children.concat(seriesIds),
                  };
                } else {
                  return node;
                }
              });

              return newTree;
            });
          });
      } else {
        // Load the nodes into the tree with existing data
        setNavTree((previousNavTree) => {
          let seriesIds = [];

          neededSeriesIds
            .map((seriesId) => series[seriesId])
            .forEach((series) => {
              let assessments = Object.values(versions).filter(
                (v) => v.assessment_series_id === series.id
              );
              let seriesNodeId = `series-${series.id}-for-deployment-${props.element.originalId}`;
              let hasSeriesNode = previousNavTree.some((n) => n.id === seriesNodeId);

              if (!hasSeriesNode) {
                let versionIds = [];
                assessments.forEach((version) => {
                  let versionNodeId = `version-${version.id}-for-series-${series.id}-for-deployment-${props.element.originalId}`;

                  // Update tree navigation with version nodes
                  versionIds.push(versionNodeId);
                  previousNavTree.push({
                    type: "version",
                    originalId: version.id,
                    name: `Version ${version.version}`,
                    id: versionNodeId,
                    parent: seriesNodeId,
                    children: [],
                    iconPath: "svg/ico-rocket-colored.svg",
                    isBranch: false,
                  });
                });

                // Update tree navigation with series nodes
                seriesIds.push(seriesNodeId);
                previousNavTree.push({
                  type: "series",
                  originalId: series.id,
                  name: series.name,
                  id: seriesNodeId,
                  parent: props.element.id,
                  children: versionIds,
                  iconPath: "svg/ico-rocket-colored.svg",
                  isBranch: true,
                });
              }
            });

          // Update tree navigation pce node with series children
          const newTree = previousNavTree.map((node) => {
            if (node.id === props.element.id) {
              return {
                ...node,
                children: node.children.concat(seriesIds),
              };
            } else {
              return node;
            }
          });

          return newTree;
        });
      }
    }

    if (props.element.type === "series") {
      // console.log("loading stuff for series", { props });
    }

    if (props.element.type === "version") {
      // console.log("loading stuff for version", { props });
    }
  };

  const handleNodeSelect = async ({ element, ...rest }) => {
    // Prevent selection while loading
    if (isFetching) {
      return;
    }

    setActiveNodeId(element.id);

    if (element.type === "root") {
      return handleSelectAccount({ id: selectedAccountId });
    }

    // If clicking on individual deployment, load attempt level data --- for deployment
    if (element.type === "deployment") {
      const deploymentIds = Object.values(deployments)
        .filter((d) => d.pce_id === element.originalId)
        .map((d) => d.id);
      const neededDeploymentIds = deploymentIds.filter(
        (deploymentId) => !Object.values(attempts).some((a) => a.deployment_id === deploymentId)
      );

      if (neededDeploymentIds.length > 0) {
        setIsFetching(true);
        setTableState({
          isLoading: true,
          columns: attemptsGroupedBySeriesColumns,
          data: [],
        });

        const params = new URLSearchParams();
        neededDeploymentIds.forEach((deploymentId, index) => {
          params.append(`deploymentIds[${index}]`, deploymentId);
        });
        const url = `${apiUrl}/rest/attempts`;
        const rows = await fetchLargeDataset({
          url,
          searchParams: params,
          pageSize: 10000,
        });

        // Add attempts for this/these deployments to local state
        setAttempts((previous) => {
          rows.forEach((a) => {
            previous[a.id] = a;
          });

          return previous;
        });

        setIsFetching(false);
        setTableState({
          isLoading: false,
          columns: attemptsGroupedBySeriesColumns,
          data: rows.map((a) => {
            return {
              ...a,
              series: series[a.assessment_series_id],
              version: versions[a.assessment_id],
            };
          }),
          additionalProps: {
            defaultGroupBy: ["series"],
          },
        });
      } else {
        const dataFromState = Object.values(attempts)
          .filter((a) => deploymentIds.includes(a.deployment_id))
          .map((a) => {
            return {
              ...a,
              series: series[a.assessment_series_id],
              version: versions[a.assessment_id],
            };
          });
        setIsFetching(false);
        setTableState({
          isLoading: false,
          columns: attemptsGroupedBySeriesColumns,
          data: dataFromState,
          additionalProps: {
            defaultGroupBy: ["series"],
          },
        });
      }
    }

    // If clicking on deployment seres, load attempt level data --- for deployment / series
    if (element.type === "series") {
      const parentDeploymentNode = navTree.find((node) => node.id === element.parent);
      const deploymentIds = Object.values(deployments)
        .filter((d) => d.pce_id === parentDeploymentNode.originalId)
        .map((d) => d.id);
      const dataFromState = Object.values(attempts)
        .filter((a) => deploymentIds.includes(a.deployment_id))
        .filter((a) => a.assessment_series_id === element.originalId)
        .map((a) => {
          return {
            ...a,
            series: series[a.assessment_series_id],
            version: versions[a.assessment_id],
          };
        });

      setIsFetching(false);
      setTableState({
        isLoading: false,
        columns: attemptsGroupedByVersionColumns,
        data: dataFromState,
        additionalProps: {
          defaultGroupBy: ["version"],
        },
      });
    }

    // If clicking on assessment version, load question response level data --- for the deployment / series / version
    if (element.type === "version") {
      const parentSeriesNode = navTree.find((node) => node.id === element.parent);
      const parentDeploymentNode = navTree.find((node) => node.id === parentSeriesNode.parent);
      const deploymentId = Object.values(deployments)
        .filter((d) => d.pce_id === parentDeploymentNode.originalId)
        .find((d) => d.assessment_series_id === parentSeriesNode.originalId).id;

      const attemptIds = Object.values(attempts)
        .filter((a) => a.deployment_id === deploymentId)
        .filter((a) => a.assessment_series_id === parentSeriesNode.originalId)
        .filter((a) => a.assessment_id === element.originalId)
        .map((a) => a.id);

      const responsesByAttemptId = Object.values(responses).reduce((map, response) => {
        let attemptId = response.attempt_id;
        let attemptExists = !!map[attemptId];

        if (attemptExists) {
          map[attemptId].push(response);
        } else {
          map[attemptId] = [response];
        }

        return map;
      }, {});

      const neededAttemptIds = attemptIds.filter((id) => {
        return responsesByAttemptId[id] ? responsesByAttemptId[id].length === 0 : true;
      });

      if (neededAttemptIds.length > 0) {
        setIsFetching(true);
        setTableState({
          isLoading: true,
          columns: responsesGroupedByAttemptColumns,
          data: [],
          additionalProps: {
            defaultGroupBy: ["username"],
          },
        });

        const params = new URLSearchParams();
        params.append("deploymentId", deploymentId);
        params.append("assessmentSeriesId", parentSeriesNode.originalId);
        params.append("assessmentId", element.originalId);

        const url = `${apiUrl}/rest/attempt-responses`;
        const rows = await fetchLargeDataset({
          url,
          searchParams: params,
          pageSize: 1000,
        });

        const matchingAssessment = versions[element.originalId];

        const attemptResponsesForAllQuestions = rows.reduce((responseMap, attempt) => {
          const matchingAttempt = attempts[attempt.id];

          matchingAssessment.questions.forEach((question, index) => {
            const uniqueResponseId = `${attempt.id}_${question.id}`;
            const matchingResponse = attempt.attempt_question_responses.find(
              (response) => String(response.question_id) === String(question.id)
            ) ?? {
              attempt_id: attempt.id,
              is_correct: false,
              question_id: question.id,
              answer_id: null,
            };
            const matchingAnswer = matchingResponse.answer_id
              ? question.answers.find((a) => String(a.id) === String(matchingResponse.answer_id))
              : null;

            responseMap[uniqueResponseId] = {
              ...matchingResponse,
              id: uniqueResponseId,
              version_id: element.originalId,
              attempt: matchingAttempt ?? {},
              question: question,
              answer: matchingAnswer ?? {},
            };
          });

          return responseMap;
        }, {});

        setResponses((previous) => {
          Object.keys(attemptResponsesForAllQuestions).forEach((responseId) => {
            previous[responseId] = attemptResponsesForAllQuestions[responseId];
          });

          return previous;
        });

        const tableData = Object.values(attemptResponsesForAllQuestions);

        setIsFetching(false);
        setTableState({
          isLoading: false,
          columns: responsesGroupedByAttemptColumns,
          data: tableData,
          additionalProps: {
            defaultGroupBy: ["username"],
          },
        });
      } else {
        const dataFromState = attemptIds.reduce((list, id) => {
          return list.concat(responsesByAttemptId[id]);
        }, []);

        setIsFetching(false);
        setTableState({
          isLoading: false,
          columns: responsesGroupedByAttemptColumns,
          data: dataFromState,
          additionalProps: {
            defaultGroupBy: ["username"],
          },
        });
      }
    }
  };

  const handleSelectAccount = (selectedAccount) => {
    const accountId = selectedAccount?.id || null;
    setSelectedAccountId(accountId);

    const haveDeploymentsForAccount = Object.values(deployments).some(
      (d) => d.account_id === accountId
    );

    if (accountId && !haveDeploymentsForAccount) {
      setTableState({
        isLoading: true,
        data: [],
        columns: deploymentColumns,
        additionalProps: {},
      });

      // Loading state for tree navigation
      setNavTree([
        // Invisible root node
        {
          id: 0,
          type: "root",
          name: "",
          children: [1],
          parent: null,
          isBranch: false,
        },
        // All deployments parent with loading message
        {
          id: 1,
          type: "root",
          name: "Deployments - Loading...",
          children: [],
          parent: 0,
          isBranch: false,
        },
      ]);

      const params = new URLSearchParams();
      params.append("accountId", selectedAccount.id);
      params.append("expand[0]", "cohorts");
      params.append("expand[1]", "series");

      const url = `${apiUrl}/rest/deployments?${params.toString()}`;
      fetch(url, {
        mode: "cors",
        method: "get",
        headers: {
          Authorization: getAccessToken(),
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((result) => {
          // Add deployments for this account to local state
          setDeployments((previous) => {
            result.data.forEach((d) => {
              previous[d.id] = d;
            });

            return previous;
          });

          // Re-render tree navigation with only deployments from this account
          const newTreeData = result.data.reduce((tree, deployment) => {
            const rootNode = tree.find((node) => node.id === 0);
            const hasRootNode = !!rootNode;
            const rootDeploymentsNode = tree.find((node) => node.id === 1);
            // Program cohort enrollment can have multiple deployments
            // But they will be tied by the pce_id and name
            const deploymentNodeId = `deployment-${deployment.pce_id}`;
            const associatedPCE = tree.find((node) => node.id === deploymentNodeId);
            const hasPCE = !!associatedPCE;

            // Listed as "deployment" but tied M:1 w/ program_cohort_enrollments
            if (!hasPCE) {
              tree.push({
                type: "deployment",
                originalId: deployment.pce_id,
                name: deployment.name,
                id: deploymentNodeId,
                parent: 1,
                children: [],
                iconPath: "svg/ico-rocket-colored.svg",
                isBranch: true,
              });
            }

            if (hasRootNode) {
              const rootHasDeployment = rootDeploymentsNode.children.includes(deploymentNodeId);

              if (!rootHasDeployment) {
                rootDeploymentsNode.children.push(deploymentNodeId);
              }
            } else {
              // All deployments parent
              tree.push({
                id: 1,
                type: "root",
                name: "Deployments",
                children: [deploymentNodeId],
                parent: 0,
                isBranch: false,
              });

              // Invisible root node
              tree.push({
                id: 0,
                type: "root",
                children: [1],
                parent: null,
                isBranch: false,
              });
            }
            return tree;
          }, []);
          setNavTree(newTreeData);
          setTableState({
            isLoading: false,
            data: result.data,
            columns: deploymentColumns,
            additionalProps: {},
          });
        });
    } else if (accountId && haveDeploymentsForAccount) {
      // Use deployments from local state
      const localDeploymentsForAccount = Object.values(deployments).filter(
        (d) => d.account_id === accountId
      );

      // Re-render tree navigation with only deployments from this account
      const newTreeData = localDeploymentsForAccount.reduce((tree, deployment) => {
        const rootNode = tree.find((node) => node.id === 0);
        const hasRootNode = !!rootNode;
        const rootDeploymentsNode = tree.find((node) => node.id === 1);
        // Program cohort enrollment can have multiple deployments
        // But they will be tied by the pce_id and name
        const associatedPCE = tree.find((node) => node.id === `pce-${deployment.pce_id}`);
        const hasPCE = !!associatedPCE;

        // Listed as "deployment" but tied M:1 w/ program_cohort_enrollments
        if (!hasPCE) {
          tree.push({
            type: "deployment",
            originalId: deployment.pce_id,
            name: deployment.name,
            id: `pce-${deployment.pce_id}`,
            parent: 1,
            children: [],
            iconPath: "svg/ico-rocket-colored.svg",
            isBranch: true,
          });
        }

        if (hasRootNode) {
          const rootHasDeployment = rootDeploymentsNode.children.includes(
            `pce-${deployment.pce_id}`
          );

          if (!rootHasDeployment) {
            rootDeploymentsNode.children.push(`pce-${deployment.pce_id}`);
          }
        } else {
          // All deployments parent
          tree.push({
            id: 1,
            type: "root",
            name: "Deployments",
            children: [`pce-${deployment.pce_id}`],
            parent: 0,
            isBranch: false,
          });

          // Invisible root node
          tree.push({
            id: 0,
            type: "root",
            name: "",
            children: [1],
            parent: null,
            isBranch: false,
          });
        }
        return tree;
      }, []);
      setNavTree(newTreeData);
      setTableState({
        isLoading: false,
        data: localDeploymentsForAccount,
        columns: deploymentColumns,
        additionalProps: {},
      });
    } else {
      setNavTree([]);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { columns, data: rows } = tableState;
      const fileDate = moment().format("MM-DD-YYYY");
      const accountName = accounts.find((account) => account.id === selectedAccountId)?.name;
      const fileName = `${accountName}_${tableTitle}_${fileDate}`;
      const headers = columns.reduce((headers, column) => {
        headers[column.id] = column.Header;

        return headers;
      }, {});
      const orderedKeys = columns.map((column) => column.id);
      const columnWidths = columns.map((column) => ({ wch: column.minWidth ?? 150 }));

      const preparedRows = rows.map((row) => {
        let newRow = {};
        orderedKeys.forEach((key) => {
          let matchingColumn = columns.find((column) => column.id === key);

          if (typeof matchingColumn.accessor === "function") {
            newRow[key] = matchingColumn.accessor(row);
          } else {
            newRow[key] = row[key];
          }
        });

        return newRow;
      });

      // const ws = utils.json_to_sheet(preparedRows, {
      //   header: orderedKeys,
      //   dense: true, // https://github.com/SheetJS/sheetjs/issues/2750
      // });
      // utils.sheet_add_aoa(ws, [orderedKeys.map((key) => headers[key])], {
      //   origin: "A1",
      // });
      // ws["!cols"] = columnWidths;
      // const wb = utils.book_new();
      // utils.book_append_sheet(wb, ws, "Export Data");
      // writeFile(wb, `${fileName}.csv`);

      const blob = new Blob([ExportWorker], { type: "text/javascript" });
      const worker = new Worker(URL.createObjectURL(blob), { type: "module" });

      worker.onmessage = function(event) {
        if (event.data.error) {
          console.error(event.data.error);
          return;
        }

        const a = document.createElement("a");
        a.download = `${fileName}.csv`;
        a.href = event.data.url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setIsExporting(false);
      };

      worker.postMessage({
        columnWidths,
        headers,
        orderedKeys,
        rows: preparedRows,
      });
    } catch (error) {
      console.error(error);
      setIsExporting(false);
    }
  };

  return (
    <DocumentTitle title="Data Explorer | Stemify">
      <SectionTable style={{ padding: "10px", flexGrow: 1 }}>
        <SectionBody
          style={{
            display: "grid",
            gap: "16px",
            padding: "40px",
            gridTemplateColumns: "350px 1fr",
            gridTemplateRows: "100%",
            minHeight: "750px",
          }}
        >
          <div style={{ borderRight: "1px solid lightgray", overflowY: "auto" }}>
            <h3>Navigation</h3>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "12px",
              }}
            >
              <Field
                isSelect
                id="account"
                name="account"
                value={accounts
                  .map((a) => ({ id: a.id, value: a.id, label: a.name }))
                  .find((a) => a.id === selectedAccountId)}
                onChange={handleSelectAccount}
                options={accounts.map((a) => ({ id: a.id, value: a.id, label: a.name }))}
                placeholder={accounts.length > 0 ? "Select an account" : "Loading..."}
                disabled={accounts.length === 0}
                style={{ minWidth: "300px", width: "fit-content" }}
              />
            </div>

            {navTree.length > 0 ? (
              <div style={{ paddingLeft: "20px" }}>
                <TreeView
                  data={navTree}
                  onLoadData={onLoadData}
                  onNodeSelect={handleNodeSelect}
                  propagateCollapse={true}
                  clickAction="SELECT"
                  defaultExpandedIds={[1]}
                  selectedIds={[activeNodeId]}
                  nodeRenderer={({ element, isBranch, isExpanded, getNodeProps, level }) => {
                    return (
                      <div
                        {...getNodeProps()}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginLeft: 30 * (level - 1),
                          position: "relative",
                          gap: isExpanded && element.children.length === 0 ? "4px" : "2px",
                        }}
                      >
                        {isBranch ? (
                          isExpanded && element.children.length === 0 ? (
                            <AiOutlineLoading aria-hidden={true} className="loading-icon" />
                          ) : (
                            <ArrowIcon isOpen={isExpanded} />
                          )
                        ) : null}

                        <div className="tree-node">
                          <div className={cx("tree-node__name", {})}>
                            {element.iconPath && <IconSvgComponent iconPath={element.iconPath} />}
                            {element.icon && <element.icon className="tree-node__icon" />}
                            <span>{element.name}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              </div>
            ) : (
              <div>No Deployments Loaded</div>
            )}
          </div>

          <div style={{ paddingLeft: "20px", paddingRight: "20px", paddingBottom: "50px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
                padding: "0px 12px",
              }}
            >
              <h3 style={{ margin: "0px" }}>{tableTitle}</h3>
              <Button
                variant="outlined"
                onClick={handleExport}
                isLoading={isExporting}
                disabled={isExporting}
              >
                Export
              </Button>
            </div>
            <div>
              <VirtualTable
                skipAnimationFrameInResizeObserver={true}
                data={tableState.data}
                columns={tableState.columns}
                isLoading={tableState.isLoading}
                enableGrouping={true}
                NoDataComponent={() => (
                  <div style={{ height: "487px", padding: "65px 24px" }}>
                    <NoDataComponent type="Deployment" iconPath="svg/ico-rocket-dark-lg.svg" />
                  </div>
                )}
                showPagination={tableState.data.length >= 10}
                style={{ fontSize: "14px", fontWeight: 500 }}
                {...tableState.additionalProps}
              />
            </div>
          </div>
        </SectionBody>
      </SectionTable>
    </DocumentTitle>
  );
};

export default AdminPage;
